import { useHistory, useLocation } from "react-router-dom";
import qs from "qs";

/**
 * 管理 URL Query String 的 Hook。
 *
 * ⚠️ 設計注意事項：
 * - `setQuery`（replace=true）使用 `window.history.replaceState`，
 *   只更新瀏覽器 URL，**不會觸發 React Router re-render**。
 * - 因此 `searchParams`（來自 `useLocation`）在 `setQuery` 後不會立即更新，
 *   是 stale 的值，直到下一次 React Router 導航才會同步。
 *
 * ✅ 適用情境：
 * - 純粹同步 URL（書籤、重整還原），不依賴 `searchParams` 做 render 判斷。
 * - 配合自己的 local state 管理即時條件（參考 `useOverview` 的 `localSearchParams` 做法）。
 *
 * ❌ 不適用：
 * - 呼叫 `setQuery` 後，直接用 `searchParams` 判斷要 render 什麼內容。
 *   → 這時 `searchParams` 是 stale 的，應自行維護 local state。
 */
const useQueryManager = () => {
	const history = useHistory();
	const location = useLocation();

	// 從 location.search 解析並回傳 query 物件
	const getQuery = () => {
		const queryString = (location.search || "").slice(1);
		return queryString ? qs.parse(queryString) : {};
	};

	// 決定是否要進入頁面時抓取資料
	const setQuery = (queryObject, replace = true) => {
		const queryString = qs.stringify(queryObject, {
			arrayFormat: "repeat",
			skipNulls: true,
		});
		const url = location.pathname + (queryString ? "?" + queryString : "");
		replace ? window.history.replaceState(null, "", url) : history.push(url);
	};

	return { searchParams: getQuery(), setQuery, getQuery };
};

export default useQueryManager;
